import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Alert,
    AlertTitle,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faCircleCheck,
    faCircleXmark,
    faPlugCircleCheck,
    faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import {useSnackbar} from 'notistack';

import {useApplicationContext} from 'ApplicationContext';
import Title from 'Components/Title';
import {
    LabelConnectSettingsDto,
    LabelConnectTestResultDto,
    UpdateLabelConnectSettingsRequest,
} from 'apiClient/Api';

/**
 * Vorgabewerte für ein leeres Formular. Bewusst harmlos: abgeschaltet, kein Schreibzugriff.
 */
const emptyForm: UpdateLabelConnectSettingsRequest = {
    enabled: false,
    baseUrl: '',
    apiKey: null,
    timeoutSeconds: 15,
    allowWrites: false,
    employeeCacheMinutes: 10,
    defaultRangeDays: 30,
    maxRangeDays: 366,
    timeZone: 'Europe/Berlin',
};

const toForm = (dto: LabelConnectSettingsDto): UpdateLabelConnectSettingsRequest => ({
    enabled: dto.enabled ?? false,
    baseUrl: dto.baseUrl ?? '',
    // null heißt "gespeicherten Schlüssel unverändert lassen" — nie den alten Wert vortäuschen.
    apiKey: null,
    timeoutSeconds: dto.timeoutSeconds ?? 15,
    allowWrites: dto.allowWrites ?? false,
    employeeCacheMinutes: dto.employeeCacheMinutes ?? 10,
    defaultRangeDays: dto.defaultRangeDays ?? 30,
    maxRangeDays: dto.maxRangeDays ?? 366,
    timeZone: dto.timeZone ?? 'Europe/Berlin',
});

/**
 * Einrichtung der Labelwin-Anbindung über den LabelConnect-Dienst. Speist den Kalender unter
 * `/kalender` und die Zielwerte, mit denen LabelConnect in Projdat schreibt.
 *
 * Alle Werte greifen ohne Server-Neustart: sie liegen in `ServerSettings` und überlagern die
 * Startwerte aus `appsettings.json`. **Das ist hier keine Feinheit, sondern der einzige Weg** —
 * die `appsettings.json` des Produktivservers wird vom Deployment nicht überschrieben, ein
 * `LabelConnect`-Abschnitt aus dem Repo kommt dort also nie an.
 */
export const LabelConnectSettings: React.FC = () => {
    const {api, apiError, setTitle} = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();

    const [settings, setSettings] = useState<LabelConnectSettingsDto | null>(null);
    const [form, setForm] = useState<UpdateLabelConnectSettingsRequest>(emptyForm);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<LabelConnectTestResultDto | null>(null);

    useEffect(() => setTitle('Labelwin-Anbindung'), [setTitle]);

    const reload = useCallback(() => {
        setLoading(true);
        api.labelConnect
            .labelConnectGetSettings()
            .then((r) => {
                setSettings(r.data);
                setForm(toForm(r.data));
            })
            .catch(apiError)
            .finally(() => setLoading(false));
    }, [api, apiError]);

    useEffect(() => reload(), [reload]);

    const patch = (changes: Partial<UpdateLabelConnectSettingsRequest>) =>
        setForm((prev) => ({...prev, ...changes}));

    /** Klientseitige Vorprüfung — dieselben Grenzen, die der Server durchsetzt. */
    const problems = useMemo(() => {
        const out: string[] = [];
        const url = (form.baseUrl ?? '').trim();

        if (url && !/^https?:\/\/.+/i.test(url)) {
            out.push('Die Basis-URL muss mit http:// oder https:// beginnen.');
        }
        if (form.enabled && !url) {
            out.push('Ohne Basis-URL lässt sich die Anbindung nicht einschalten.');
        }
        if ((form.timeoutSeconds ?? 0) < 1 || (form.timeoutSeconds ?? 0) > 120) {
            out.push('Das Zeitlimit muss zwischen 1 und 120 Sekunden liegen.');
        }
        if ((form.maxRangeDays ?? 0) < 1 || (form.maxRangeDays ?? 0) > 366) {
            out.push('Die Obergrenze des Zeitfensters muss zwischen 1 und 366 Tagen liegen.');
        }
        if ((form.defaultRangeDays ?? 0) < 0 || (form.defaultRangeDays ?? 0) >= (form.maxRangeDays ?? 0)) {
            out.push('Die Vorgabespanne muss kleiner als die Obergrenze sein.');
        }
        if (!(form.timeZone ?? '').trim()) {
            out.push('Die Zeitzone darf nicht leer sein (IANA-Kennung, z. B. Europe/Berlin).');
        }
        return out;
    }, [form]);

    const save = async (): Promise<void> => {
        setSaving(true);
        try {
            const response = await api.labelConnect.labelConnectSaveSettings(form);
            setSettings(response.data);
            setForm(toForm(response.data));
            setTestResult(null);
            enqueueSnackbar('Gespeichert — greift ohne Neustart.', {variant: 'success'});
        } catch (e: any) {
            // Der Server antwortet bei Validierungsfehlern mit Klartext.
            const detail = e?.response?.status === 400 ? e?.response?.data : null;
            if (typeof detail === 'string' && detail.length > 0) {
                enqueueSnackbar(detail, {variant: 'error'});
            } else {
                apiError(e);
            }
        } finally {
            setSaving(false);
        }
    };

    const runTest = async (): Promise<void> => {
        setTesting(true);
        setTestResult(null);
        try {
            const response = await api.labelConnect.labelConnectTestConnection();
            setTestResult(response.data);
        } catch (e) {
            apiError(e);
        } finally {
            setTesting(false);
        }
    };

    const active = (settings?.enabled ?? false) && (settings?.isComplete ?? false);
    const hasApiKey = settings?.apiKeyConfigured ?? false;

    return (
        <Grid size={12}>
            <Title>Labelwin-Anbindung (LabelConnect)</Title>
            {loading && <LinearProgress sx={{mb: 2}}/>}

            <Grid container spacing={2}>
                <Grid size={12}>
                    <Paper sx={{p: 2}}>
                        <Stack direction={{xs: 'column', md: 'row'}} spacing={2}
                               sx={{alignItems: {md: 'center'}, justifyContent: 'space-between'}}>
                            <Stack direction="row" spacing={1} sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 1}}>
                                <FontAwesomeIcon icon={active ? faCircleCheck : faCircleXmark}/>
                                <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                                    {active ? 'Anbindung aktiv' : 'Anbindung inaktiv'}
                                </Typography>
                                {settings?.enabled && !settings?.isComplete && (
                                    <Chip size="small" color="warning" label="unvollständig konfiguriert"/>
                                )}
                                {settings?.baseUrl && <Chip size="small" variant="outlined" label={settings.baseUrl}/>}
                                <Chip size="small" variant="outlined"
                                      color={settings?.allowWrites ? 'warning' : 'default'}
                                      label={settings?.allowWrites ? 'Schreibzugriff erlaubt' : 'nur lesend'}/>
                                {settings?.timeZone && (
                                    <Chip size="small" variant="outlined" label={settings.timeZone}/>
                                )}
                            </Stack>
                            <Button variant="outlined" onClick={runTest} disabled={testing || loading}
                                    startIcon={<FontAwesomeIcon icon={faPlugCircleCheck}/>}>
                                {testing ? 'Teste …' : 'Verbindung testen'}
                            </Button>
                        </Stack>

                        <Typography variant="body2" color="text.secondary" sx={{mt: 1.5}}>
                            Brücke zum Labelwin-ERP (Projdat). Speist den Kalender unter „Kalender" und die
                            Auswahlliste für den LabelBenutzer in der Benutzerverwaltung. Der Kalender zeigt immer
                            den des angemeldeten Benutzers — wer keinen LabelBenutzer hinterlegt hat, sieht ihn nicht.
                        </Typography>

                        {settings?.apiKeyUnreadable && (
                            <Alert severity="warning" sx={{mt: 2}}>
                                Es liegt ein gespeicherter API-Schlüssel vor, der sich nicht entschlüsseln lässt —
                                typisch nach neu erzeugten DataProtection-Keys. Bitte neu eintragen.
                            </Alert>
                        )}

                        {testResult && (
                            <Alert severity={testResult.success ? 'success' : 'error'} sx={{mt: 2}}>
                                <AlertTitle>
                                    {testResult.success ? 'Verbindung erfolgreich' : 'Verbindung fehlgeschlagen'}
                                </AlertTitle>
                                {testResult.message}
                                {testResult.matchedEmployee && (
                                    <Typography variant="body2" sx={{mt: 1}}>
                                        Ihre Zuordnung: {testResult.matchedEmployee.name} (Personalnummer{' '}
                                        {testResult.matchedEmployee.employeeId})
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1}}>
                                    Dauer: {testResult.elapsedMilliseconds} ms
                                    {testResult.employeeCount != null && ` · ${testResult.employeeCount} Mitarbeiter`}
                                </Typography>
                            </Alert>
                        )}
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Paper sx={{p: 2}}>
                        <Stack spacing={2}>
                            <Typography variant="subtitle1" sx={{fontWeight: 600}}>Verbindung</Typography>

                            <FormControlLabel
                                control={<Checkbox checked={form.enabled ?? false}
                                                   onChange={(e) => patch({enabled: e.target.checked})}/>}
                                label="Anbindung aktiv"/>

                            <Grid container spacing={2}>
                                <Grid size={{xs: 12, md: 8}}>
                                    <TextField fullWidth size="small" label="Basis-URL"
                                               value={form.baseUrl ?? ''}
                                               onChange={(e) => patch({baseUrl: e.target.value})}
                                               placeholder="http://192.168.1.20:5000"
                                               helperText="Produktiv Port 5000 — die 5282 aus der LabelConnect-Doku ist nur dessen Dev-Profil."/>
                                </Grid>
                                <Grid size={{xs: 12, md: 4}}>
                                    <TextField fullWidth size="small" type="number" label="Zeitlimit (s)"
                                               value={form.timeoutSeconds ?? 15}
                                               onChange={(e) => patch({timeoutSeconds: Number(e.target.value)})}
                                               helperText="1–120"/>
                                </Grid>

                                <Grid size={{xs: 12, md: 8}}>
                                    <Stack direction="row" spacing={1} sx={{alignItems: 'flex-start'}}>
                                        <TextField
                                            fullWidth size="small" type="password" label="API-Schlüssel (optional)"
                                            value={form.apiKey ?? ''}
                                            disabled={form.apiKey === null && hasApiKey}
                                            onChange={(e) => patch({apiKey: e.target.value})}
                                            placeholder={hasApiKey ? '•••••••• (gespeichert)' : 'LabelConnect läuft derzeit ohne Authentifizierung'}
                                            helperText={form.apiKey === null && hasApiKey
                                                ? 'Hinterlegt. Zum Ersetzen auf „Ändern" klicken — der gespeicherte Wert wird nie ausgeliefert.'
                                                : 'Wird als Header X-Api-Key gesendet, verschlüsselt gespeichert. Leer lassen, solange LabelConnect ohne Token läuft.'}/>
                                        {hasApiKey && (
                                            <Button size="small" sx={{mt: 0.5}}
                                                    onClick={() => patch({apiKey: form.apiKey === null ? '' : null})}>
                                                {form.apiKey === null ? 'Ändern' : 'Abbrechen'}
                                            </Button>
                                        )}
                                    </Stack>
                                </Grid>
                                <Grid size={{xs: 12, md: 4}}>
                                    <TextField fullWidth size="small" label="Zeitzone der Plantafel"
                                               value={form.timeZone ?? ''}
                                               onChange={(e) => patch({timeZone: e.target.value})}
                                               helperText="IANA-Kennung, z. B. Europe/Berlin"/>
                                </Grid>
                            </Grid>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Paper sx={{p: 2}}>
                        <Stack spacing={2}>
                            <Typography variant="subtitle1" sx={{fontWeight: 600}}>Kalender</Typography>

                            <Grid container spacing={2}>
                                <Grid size={{xs: 12, md: 4}}>
                                    <TextField fullWidth size="small" type="number" label="Vorgabespanne (Tage)"
                                               value={form.defaultRangeDays ?? 30}
                                               onChange={(e) => patch({defaultRangeDays: Number(e.target.value)})}
                                               helperText="Zeitfenster ohne Angabe von/bis"/>
                                </Grid>
                                <Grid size={{xs: 12, md: 4}}>
                                    <TextField fullWidth size="small" type="number" label="Obergrenze (Tage)"
                                               value={form.maxRangeDays ?? 366}
                                               onChange={(e) => patch({maxRangeDays: Number(e.target.value)})}
                                               helperText="Mehr nimmt LabelConnect nicht an"/>
                                </Grid>
                                <Grid size={{xs: 12, md: 4}}>
                                    <TextField fullWidth size="small" type="number"
                                               label="Mitarbeiterliste puffern (min)"
                                               value={form.employeeCacheMinutes ?? 10}
                                               onChange={(e) => patch({employeeCacheMinutes: Number(e.target.value)})}
                                               helperText="0 = nicht puffern"/>
                                </Grid>
                            </Grid>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Paper sx={{p: 2}}>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                                <FontAwesomeIcon icon={faTriangleExclamation}/>
                                <Typography variant="subtitle1" sx={{fontWeight: 600}}>Schreibzugriff</Typography>
                            </Stack>

                            {/*
                              * Der Schalter steht bewusst allein und mit Begründung: Projdat hat auf der
                              * Plantafel weder Trigger noch Fremdschlüssel, ein Fehler erzeugt dort keine
                              * Meldung, sondern einen falschen Termin.
                              */}
                            <Alert severity={form.allowWrites ? 'warning' : 'info'}>
                                <AlertTitle>Vor dem Einschalten abnehmen</AlertTitle>
                                Die Schreibpfade von LabelConnect haben noch keinen Satz in Projdat geschrieben, und
                                die Plantafel hat weder Trigger noch Fremdschlüssel — ein Fehler fällt dort nicht als
                                Fehlermeldung auf, sondern als falscher Termin. Prüfen Sie erst im Labelwin-Client:
                                Termin über den Kalender anlegen, dort nachsehen, verschieben, löschen.
                            </Alert>

                            <FormControlLabel
                                control={<Checkbox checked={form.allowWrites ?? false}
                                                   onChange={(e) => patch({allowWrites: e.target.checked})}/>}
                                label="Anlegen, Verschieben und Löschen von Kalendereinträgen erlauben"/>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid size={12}>
                    {problems.length > 0 && (
                        <Alert severity="warning" sx={{mb: 2}}>
                            <AlertTitle>Bitte prüfen</AlertTitle>
                            {problems.map((problem) => <div key={problem}>{problem}</div>)}
                        </Alert>
                    )}
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" onClick={save}
                                disabled={saving || loading || problems.length > 0}>
                            {saving ? 'Speichere …' : 'Speichern'}
                        </Button>
                        <Button variant="text" onClick={reload} disabled={saving || loading}>
                            Verwerfen
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default LabelConnectSettings;
